import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff/60)}min`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const sourceColor = (s) => ({
  djen: "text-rose-700 bg-rose-50",
  radar: "text-amber-700 bg-amber-50",
  prazos: "text-red-700 bg-red-50",
  cobrancas: "text-emerald-700 bg-emerald-50",
}[s] || "text-slate-700 bg-slate-100");

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [anchor, setAnchor] = useState(null); // {top, right} viewport-relative
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const prevUnreadRef = useRef(null);

  const updateAnchor = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };

  const load = async () => {
    try {
      const r = await api.get("/notifications/timeline?limit=10");
      setItems(r.data?.items || []);
      const u = (r.data?.counts?.total_unread) ?? r.data?.total_unread
              ?? (r.data?.items || []).filter(i => !i.read).length;
      setUnread(u);
      // Toast on new unread (skip first load)
      if (prevUnreadRef.current != null && u > prevUnreadRef.current) {
        const delta = u - prevUnreadRef.current;
        toast(`${delta} nova${delta > 1 ? "s" : ""} notifica${delta > 1 ? "ções" : "ção"}`, {
          description: "Confira seus alertas mais recentes.",
          action: { label: "Abrir", onClick: () => setOpen(true) },
        });
      }
      prevUnreadRef.current = u;
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    updateAnchor();
    const onDoc = (e) => {
      const btn = btnRef.current;
      const menu = menuRef.current;
      if (!menu) return;
      if (btn && btn.contains(e.target)) return;
      if (menu.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => updateAnchor();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${encodeURIComponent(id)}/read`); load(); } catch {}
  };
  const openItem = async (it) => {
    const nid = it.id || it.notification_id;
    // marcar lido antes de navegar (não bloqueia)
    if (!it.read && nid) {
      api.post(`/notifications/${encodeURIComponent(nid)}/read`).catch(() => {});
    }
    setOpen(false);
    const target = it.link || it.url || (it.source === "djen" ? "/djen"
        : it.source === "prazos" ? "/prazos"
        : it.source === "cobrancas" ? "/cobrancas"
        : "/notificacoes");
    window.location.assign(target);
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch {}
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        data-testid="topbar-notifications"
        className="relative w-11 h-11 rounded-xl bg-white border border-slate-200 inline-flex items-center justify-center hover:bg-slate-50 transition shadow-sm"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5 text-slate-600"/>
        {unread > 0 && (
          <span data-testid="bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold ring-2 ring-white inline-flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && anchor && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          data-testid="bell-dropdown"
          className="fixed w-96 max-w-[95vw] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
          style={{ top: anchor.top, right: anchor.right, zIndex: 2147483000 }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <div className="text-sm font-semibold text-slate-900">Notificações</div>
              <div className="text-[11px] text-slate-500">{unread} não lida{unread === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} data-testid="bell-mark-all"
                  className="text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100">
                  Marcar todas
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100" aria-label="Fechar">
                <X className="w-4 h-4 text-slate-500"/>
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">Nenhuma notificação.</div>
            ) : (
              items.map((it, i) => (
                <button key={it.notification_id || i} data-testid={`bell-item-${i}`}
                     onClick={() => openItem(it)}
                     className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${it.read ? "opacity-70" : ""}`}>
                  <div className="flex items-start gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${sourceColor(it.source)} shrink-0`}>
                      {it.source || "geral"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-900 truncate">{it.title || "—"}</div>
                      {it.subtitle && <div className="text-[11px] text-slate-600 truncate">{it.subtitle}</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">{fmt(it.at || it.created_at)}</div>
                    </div>
                    {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 shrink-0" aria-label="não lida"/>}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <Link to="/notificacoes" onClick={() => setOpen(false)}
                  className="text-xs font-semibold text-slate-700 hover:text-slate-900">
              Ver todas →
            </Link>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
